import { BlueprintType } from '@constants'
import { IBlueprint, IImportData } from '@query/documents'
import { handleReturn, PaginatedResponse, QueryFunction } from '@query/root'
import { network } from '@utils'
import { IDocumentRelation } from './documentRelations'

export enum AccountingSuggestionKey {
  AiSuggestion = 'ai-suggestion',
  Liabilities = 'open-liabilities',
  Receivables = 'open-receivables',
  PurchaseInvoice = 'purchase-invoice',
  SalesInvoice = 'sales-invoice',
  SalaryPayment = 'salary-payment',
  SalaryAccrual = 'salary-accrual',
  IdenticalTransaction = 'identical-transaction',
  SimilarDocument = 'similar-document',
  RuleWaitAttachment = 'rule-wait-attachment',
  RuleDraft = 'rule-draft'
}

export interface IAccountingSuggestionUIRow {
  account_name: string
  account_number: number
  vat_rate: number
  vat_code: number
  is_debet: boolean
  amount: number
}

interface IAccountingSuggestionUIPaymentAccount {
  id: number
  number: string
  name: string
}

export type PostingMode = 'cash' | 'accrual_split'

export interface IAccrualSplit {
  enabled: boolean
  method: PostingMode
  suggested_date: string | null
  account_number: number | null
  account_name: string | null
}

export interface IAccountingSuggestion {
  id: number
  key: AccountingSuggestionKey
  date: string
  amount: number
  description: string
  contact?: number | null
  contact_name: string
  suggested_relations: IDocumentRelation[]
  document_relations?: IDocumentRelation[]
  attachment_ids: number[]
  blueprint_type: BlueprintType
  blueprint: IBlueprint
  ui_rows: IAccountingSuggestionUIRow[]
  source_account: IAccountingSuggestionUIPaymentAccount
  accrual_split: IAccrualSplit
  import_data: IImportData
  tag_ids: number[]
  uncertainty?: string[]
  history_document_id?: number | null
  confidence?: number | null
  posting_mode?: PostingMode
  accrual_date?: string | null
}

export const getSuggestionDefaultPostingMode = (
  suggestion: Pick<IAccountingSuggestion, 'accrual_split' | 'posting_mode' | 'suggested_relations'>
): PostingMode => {
  if (!suggestion.accrual_split?.enabled) return 'cash'
  if ((suggestion.suggested_relations?.length ?? 0) > 0) return 'cash'
  return suggestion.posting_mode ?? suggestion.accrual_split?.method ?? 'cash'
}

export const getSuggestionDefaultAccrualDate = (
  suggestion: Pick<IAccountingSuggestion, 'accrual_split' | 'accrual_date'>
): string | null => {
  return suggestion.accrual_date ?? suggestion.accrual_split?.suggested_date ?? null
}

interface SuggestionProps {
  slug: string
  documentId?: number
}

export const fetchAccountingSuggestions: QueryFunction<
  SuggestionProps,
  PaginatedResponse<IAccountingSuggestion>
> = async ({ slug, documentId }) => {
  const { data } = await network.httpClient.request({
    url: `/v1/business/${slug}/accounting_suggestions/`,
    method: 'GET',
    params: { document: documentId }
  })
  return data
}

interface AcceptSuggestionProps {
  slug: string
  suggestionId: number
  modifiedData?: Partial<IAccountingSuggestionAcceptPayload>
}

export interface IAccountingSuggestionAcceptPayload {
  date?: string
  description?: string
  blueprint?: IBlueprint
  contact?: number | null
  attachment_ids?: number[]
  tag_ids?: number[]
  relations?: IDocumentRelation[]
  posting_mode?: PostingMode
  accrual_date?: string | null
  attachment_exempt?: boolean
}

export const acceptAccountingSuggestion: QueryFunction<AcceptSuggestionProps, void> = async ({
  slug,
  suggestionId,
  modifiedData
}) => {
  const { status, data } = await network.httpClient.request({
    url: `/v1/business/${slug}/accounting_suggestions/${suggestionId}/accept/`,
    method: 'POST',
    data: modifiedData,
    validateStatus: () => true
  })

  return handleReturn({ status, data })
}

interface RejectSuggestionProps {
  slug: string
  suggestionId: number
}

export const rejectAccountingSuggestion: QueryFunction<RejectSuggestionProps, void> = async ({
  slug,
  suggestionId
}) => {
  await network.httpClient.request({
    url: `/v1/business/${slug}/accounting_suggestions/${suggestionId}/reject/`,
    method: 'POST'
  })
}
